import { ArrowRight } from 'lucide-react'
import { Link } from 'react-router-dom'
import { type ReactNode } from 'react'

import { Button } from '@/components/ui/button'

/**
 * Reusable hero for every explore-bucket page.
 *
 * Layout:
 *   [bucket pill]
 *   BIG HEADLINE                       [optional mockup]
 *   sub-copy
 *   [CTA pair]
 *
 * The mockup slot lets each bucket show its lead visual immediately
 * (e.g. a roster card on Squadrons; a bracket on Tournaments) — the
 * "show, don't tell" promise of the explore pages starts at the hero.
 *
 * `bucketLabel` matches the top-nav label so visitors orient instantly:
 * "I'm on the Squadrons page" reads in the upper-left pip.
 */
export function ExploreHero({
  bucketLabel,
  headline,
  subline,
  primaryCtaLabel = 'Get started free',
  primaryCtaTo = '/signup',
  secondaryCtaLabel,
  secondaryCtaTo,
  visual,
}: {
  bucketLabel: string
  headline: ReactNode
  subline: ReactNode
  primaryCtaLabel?: string
  primaryCtaTo?: string
  secondaryCtaLabel?: string
  secondaryCtaTo?: string
  visual?: ReactNode
}) {
  return (
    <section className="relative isolate overflow-hidden border-b border-border/60 px-6 pt-20 pb-24">
      <div className="relative mx-auto grid w-full max-w-6xl items-center gap-12 lg:grid-cols-[1.05fr_1fr] lg:gap-16">
        {/* Copy column */}
        <div className="flex flex-col items-center text-center lg:items-start lg:text-left">
          {/* Bucket pill — orients the visitor at a glance. */}
          <div className="mb-7 inline-flex items-center gap-2 rounded-full border border-border bg-surface/80 px-3 py-1 font-display text-xs tracking-wider text-fg-muted uppercase backdrop-blur">
            <span
              aria-hidden
              className="inline-flex h-1.5 w-1.5 rounded-full bg-primary"
            />
            {bucketLabel}
          </div>

          <h1 className="font-display text-balance text-5xl leading-[1.02] font-bold md:text-6xl lg:text-7xl">
            {headline}
          </h1>

          <p className="mt-6 max-w-xl text-pretty text-base text-fg-muted md:text-lg">
            {subline}
          </p>

          {(primaryCtaLabel || secondaryCtaLabel) && (
            <div className="mt-9 flex w-full flex-col items-center gap-3 sm:w-auto sm:flex-row lg:items-start">
              {primaryCtaLabel && (
                <Button asChild variant="primary" size="lg" className="w-full sm:w-auto">
                  <Link to={primaryCtaTo}>
                    {primaryCtaLabel}
                    <ArrowRight className="size-4" aria-hidden />
                  </Link>
                </Button>
              )}
              {secondaryCtaLabel && secondaryCtaTo && (
                <Button
                  asChild
                  variant="outline"
                  size="lg"
                  className="font-display w-full sm:w-auto"
                >
                  <Link to={secondaryCtaTo}>{secondaryCtaLabel}</Link>
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Visual column — optional. When passed, sits in the right
            half on desktop and stacks below copy on mobile. The visual
            is typically a MockupFrame'd component. */}
        {visual && (
          <div className="relative flex items-center justify-center">
            {visual}
          </div>
        )}
      </div>
    </section>
  )
}
